import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, Typography } from 'antd';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { persistor } from '@store/index';
import { clearAuth, closeSessionExpiredModal } from '@store/slices/authSlice';

/**
 * Global modal when an authenticated API call returns 401 (session invalid / expired).
 * Mounted under BrowserRouter so navigation uses SPA routing.
 */
export function SessionExpiredModal() {
  const open = useAppSelector((s) => s.auth.sessionExpiredModalOpen);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const redirectAfterClose = useRef(false);

  const handleCancel = () => {
    redirectAfterClose.current = false;
    dispatch(closeSessionExpiredModal());
  };

  const handleGoLogin = () => {
    redirectAfterClose.current = true;
    dispatch(closeSessionExpiredModal());
  };

  const afterClose = useCallback(() => {
    if (!redirectAfterClose.current) return;
    redirectAfterClose.current = false;
    dispatch(clearAuth());
    void persistor.purge().finally(() => {
      const go = () => navigate('/login', { replace: true });
      const doc = document as Document & { startViewTransition?: (cb: () => void) => void };
      if (typeof doc.startViewTransition === 'function') {
        doc.startViewTransition(go);
      } else {
        go();
      }
    });
  }, [dispatch, navigate]);

  return (
    <Modal
      open={open}
      title="Session expired"
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>
          <Button key="login" type="primary" onClick={handleGoLogin}>
            Go to login
          </Button>
        </div>
      }
      transitionName="ant-fade"
      maskTransitionName="ant-fade"
      afterClose={afterClose}
      styles={{ body: { paddingTop: 8 } } }
    >
      <Typography.Paragraph style={{ marginBottom: 0 }}>
        Your session is no longer valid. Sign in again to continue, or cancel to stay on this page.
      </Typography.Paragraph>
    </Modal>
  );
}
